/** @jsx jsx */
import { jsx } from '@emotion/core'
import styled from '@emotion/styled'
import React, { useState, useContext, useMemo, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  getApiTargetInfo,
  getClientInfo,
  getEnvConfig,
  getPermitActions,
  getUnitNameMap,
  getUnits,
  hasAccesPermits,
  monthsMap,
} from 'core'
import { CriteriaRow } from 'core/screen-util'
import { Button, DatePicker, Input, InputNumber, Select } from 'antd'
import { DatePickerContext, ErrorContext, PNodeZoneContext } from 'context'
import * as Actions from 'core/EventActions'

import CommonScreen from 'admin/component/CommonScreen'
import { toSelectionColProps } from '@integec/grid-tools'

import MarginAnalysisUtilityUnitSelection from '../MarginAnalysisUtilityUnitSelection'
import { getPFuelCodeForOtherCostByClients } from 'marginanalysis/utils'
import getScreenInfo from './screen-info'
import moment from 'moment'
import * as R from 'ramda'

const yearMap = R.compose(
  R.fromPairs,
  R.map(a => [a, a + ''])
)(R.range(-15, 10).map(a => a + moment().year()))

const Label = styled.div`
  display: grid;
  justify-content: end;
  align-content: center;
`
const getLabel = label => <Label>{label} :</Label>
const DateComponent = ({
  style = {},
  format = 'MM/DD/YYYY',
  name,
  displayLabel,
  insertData,
  setInsertData,
  ...compProps
}) => (
  <>
    {getLabel(displayLabel)}
    <DatePicker
      allowClear={false}
      format={format}
      value={insertData[name + 'Obj']}
      onChange={date =>
        setInsertData({
          ...insertData,
          [name]: date.format('YYYY-MM-DD'),
          [name + 'Obj']: date,
        })
      }
      style={{ width: '100%', ...style }}
      {...compProps}
    />
  </>
)
const NumberComponent = ({
  style = {},
  name,
  defaultValue = 0,
  displayLabel,
  insertData,
  setInsertData,
  ...compProps
}) => (
  <>
    {getLabel(displayLabel)}
    <InputNumber
      style={{ width: '100%', ...style }}
      value={insertData[name]}
      defaultValue={defaultValue}
      onChange={value => setInsertData({ ...insertData, [name]: value })}
      {...compProps}
    />
  </>
)
const InputComponent = ({
  style = {},
  name,
  displayLabel,
  insertData,
  setInsertData,
  ...compProps
}) => (
  <>
    {getLabel(displayLabel)}
    <Input
      style={{ width: '100%', ...style }}
      value={insertData[name]}
      onChange={e => setInsertData({ ...insertData, [name]: e.target.value })}
      {...compProps}
    />
  </>
)
const DropdownComponent = ({
  style = {},
  name,
  choiceMap,
  displayLabel,
  insertData,
  setInsertData,
  ...compProps
}) => (
  <>
    {getLabel(displayLabel)}
    <Select
      value={insertData[name]}
      onChange={value => setInsertData({ ...insertData, [name]: value })}
      style={{ width: '100%', ...style }}
      {...compProps}
    >
      {(name === 'year'
        ? Object.keys(choiceMap).reverse()
        : Object.keys(choiceMap)
      ).map(m => (
        <Select.Option key={m} value={m}>
          {m === choiceMap[m] ? m : m + ' - ' + choiceMap[m]}
        </Select.Option>
      ))}
    </Select>
  </>
)

const CommonMarginImport = ({
  targetInfo,
  envConfig,
  actionPermits,
  location,
  unitNameMap,
  CLIENT,
}) => {
  const { setError } = useContext(ErrorContext)
  const [insertEvent, setInsertEvent] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [editMode, setEditMode] = useState(true)
  const [year, setYear] = useState(moment().format('YYYY'))
  const [month, setMonth] = useState('ALL')
  const [pFuelCodeMap, setPFuelCodeMap] = useState({})
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  const { unitIdEmktIdMap, marginUnitsList, emktIdUnitNameMap } = useContext(
    PNodeZoneContext
  )
  const {
    title,
    headers = [],
    criteria,
    useDefaultUnit,
    commonScreenProps,
    insertCompProps = {},
    saveTask,
    retrieveTask,
    deleteTask,
    modifySaveDataBeforeSave = false,
    beforeInsertData = false,
    extraCriteriaAfterButton = false,
  } = getScreenInfo(location.pathname.substring(1))

  const ExtraCriteriaMamo = useMemo(
    _ =>
      extraCriteriaAfterButton ? (
        extraCriteriaAfterButton({ CLIENT, targetInfo, setError })
      ) : (
        <div />
      ),
    [CLIENT, extraCriteriaAfterButton, setError, targetInfo]
  )

  const headerWithFormatter = useMemo(
    _ =>
      headers.map(header =>
        header.ident === 'unitId'
          ? {
              ...header,
              dataFormatter: ({ value }) =>
                (useDefaultUnit ? unitNameMap : emktIdUnitNameMap)[value] ||
                value,
              sortIndexGetter: ({ rowData }) =>
                (useDefaultUnit ? unitNameMap : emktIdUnitNameMap)[
                  rowData.unitId
                ] || rowData.unitId,
            }
          : header.ident === 'year'
          ? { ...header, ...toSelectionColProps(yearMap) }
          : header.ident === 'month'
          ? { ...header, ...toSelectionColProps(monthsMap) }
          : header.ident.toLowerCase().includes('fuelcode') ||
            header.ident.toLowerCase().includes('pfuelcode')
          ? {
              ...header,
              ...toSelectionColProps(pFuelCodeMap),
              dataFormatter: ({ value }) => value + ' - ' + pFuelCodeMap[value],
            }
          : header
      ),
    [emktIdUnitNameMap, headers, pFuelCodeMap, unitNameMap, useDefaultUnit]
  )

  const getComponent = ({ header, insertData, setInsertData }) => {
    const {
      ident,
      type,
      display,
      dropdown,
      innerCompProps = {},
      choices = [],
    } = header

    if (dropdown) {
      return (
        <DropdownComponent
          name={ident}
          displayLabel={display}
          choiceMap={
            ident.toLowerCase().includes('fuelcode')
              ? pFuelCodeMap
              : R.compose(
                  R.fromPairs,
                  R.map(a => [a.value, a.text])
                )(choices)
          }
          insertData={insertData}
          setInsertData={setInsertData}
          {...innerCompProps}
        />
      )
    } else if (type === 'str') {
      return (
        <InputComponent
          name={ident}
          displayLabel={display}
          insertData={insertData}
          setInsertData={setInsertData}
          {...innerCompProps}
        />
      )
    } else if (type === 'num') {
      return (
        <NumberComponent
          name={ident}
          displayLabel={display}
          insertData={insertData}
          setInsertData={setInsertData}
          {...innerCompProps}
        />
      )
    } else if (type === 'date-time') {
      return (
        <DateComponent
          name={ident}
          displayLabel={display}
          insertData={insertData}
          setInsertData={setInsertData}
          {...innerCompProps}
        />
      )
    } else {
      return null
    }
  }

  const insertComp = ({ insertData, setInsertData, onAdd, onCancel }) => {
    const {
      gridTemplateColumns = '120px 150px 200px 150px 120px 150px 120px 120px 140px 100px',
      fillerSpan = 8,
      // '80px 150px 200px 150px 100px 120px 120px 180px 100px 100px',
    } = insertCompProps

    return (
      <CriteriaRow style={{ padding: '5px', paddingLeft: '10px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gridGap: '5px',
          }}
        >
          <Label>Unit :</Label>
          <div style={{ gridColumn: 'span 3' }}>
            <MarginAnalysisUtilityUnitSelection
              utility={insertData.utility || null}
              unit={insertData.unit || null}
              onUnitChange={value =>
                setInsertData({
                  ...insertData,
                  utility: value[0],
                  unit: value[1],
                  unitId: unitIdEmktIdMap[value[1]],
                })
              }
              style={{ width: '100%' }}
            />
          </div>
          {headerWithFormatter
            .filter(
              h => !['unitId', 'modifier', 'modifiedDate'].includes(h.ident)
            )
            .map(header => getComponent({ header, insertData, setInsertData }))}
          {fillerSpan ? (
            <div style={{ gridColumn: 'span ' + fillerSpan }} />
          ) : null}
          <div
            style={{
              // gridColumn: 'span 3',
              gridRowGap: '10px',
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'auto auto auto',
              paddingLeft: '5px',
              gridColumnGap: '10px',
            }}
          >
            <Button type="primary" onClick={() => onAdd(true)}>
              Add
            </Button>
            <Button type="primary" onClick={() => onAdd(false)}>
              Add & Hide
            </Button>
            <Button type="danger" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </CriteriaRow>
    )
  }

  useEffect(
    _ => {
      getPFuelCodeForOtherCostByClients({
        targetInfo,
        CLIENT: envConfig.clientId,
        setError,
        setPFuelCodeMap,
      })
    },
    [envConfig.clientId, setError, targetInfo]
  )

  const writeEventActionPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [
          Actions.MARGIN_ANALYSIS_ADMIN,
          Actions.MARGIN_ANALYSIS_CORE_WRITE,
        ],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  const writePermission = useMemo(_ => writeEventActionPermission && editMode, [
    writeEventActionPermission,
    editMode,
  ])

  const withCriteria = (state, props, selectedUnits, selectedDate) => ({
    date: selectedDate,
    units: (selectedUnits || [])
      .filter(e => !!e)
      .map(e => (useDefaultUnit ? e.unitId : unitIdEmktIdMap[e.unitId])),
    year,
    month,
    start: selectedDateRangePicker[0],
    end: selectedDateRangePicker[1],
  })

  const screenCriteria = (s, p, u, onChange) => {
    const { showDateRange, showYear, showMonth } = criteria
    return (
      <div>
        {showDateRange && (
          <DatePicker.RangePicker
            style={{ marginLeft: '15px' }}
            allowClear={false}
            format="MM/DD/YYYY"
            value={selectedDateRangePicker}
            onChange={d => {
              setSelectedDateRagePicker(d)
              onChange()
              // retrieve({ dates: d })
            }}
          />
        )}
        {showYear && (
          <Select
            value={year}
            onChange={value => {
              setYear(value)
              onChange()
            }}
            style={{ width: '100px', marginLeft: '15px' }}
          >
            {Object.keys(yearMap)
              .reverse()
              .map(i => (
                <Select.Option key={i} value={i}>
                  {yearMap[i]}
                </Select.Option>
              ))}
          </Select>
        )}
        {showMonth && (
          <Select
            value={month}
            onChange={value => {
              setMonth(value)
              onChange()
            }}
            style={{ width: '120px', marginLeft: '15px' }}
          >
            <Select.Option key="ALL" value="ALL">
              ALL
            </Select.Option>
            {Object.keys(monthsMap).map(i => (
              <Select.Option key={i} value={i}>
                {i}-{monthsMap[i]}
              </Select.Option>
            ))}
          </Select>
        )}
      </div>
    )
  }

  const extraUnitSelectionProps = _ => ({
    limitedUnits: marginUnitsList,
    units: marginUnitsList,
    // itemToLabel: e => e.emktId,
  })

  // eslint-disable-next-line no-unused-vars
  const isEditableGrid = ({ header, rowData }) => {
    if (
      header.ident === 'modifier' ||
      header.ident === 'modifiedDate' ||
      header.isKey
    )
      return false

    return writePermission
  }

  const extProps = { CLIENT, targetInfo, setError }
  return (
    <CommonScreen
      extraCriteriaAfterButton={props =>
        extraCriteriaAfterButton && (
          <ExtraCriteriaMamo {...extProps} {...props} />
        )
      }
      beforeInsertData={beforeInsertData}
      modifySaveDataBeforeSave={modifySaveDataBeforeSave}
      withoutModify={!writeEventActionPermission}
      withDelete={!writeEventActionPermission}
      allowInsert={writeEventActionPermission}
      isEditableGrid={isEditableGrid}
      extraUnitSelectionProps={extraUnitSelectionProps}
      withUnitSelection
      autoRetrieve={false}
      isAdminScreen={false}
      withCriteria={withCriteria}
      screenCriteria={criteria ? screenCriteria : null}
      title={title}
      header={headerWithFormatter}
      saveTask={saveTask}
      retrieveTask={retrieveTask}
      deleteTask={deleteTask}
      altBy={u => u.unitId}
      sortOptions={headerWithFormatter}
      initialSortOptions={headerWithFormatter}
      widthOffset={50}
      insertComp={insertEvent && insertComp}
      insertCompHeight={120}
      onInsertEvent={b => setInsertEvent(b)}
      defaultInsertData={{ year: moment().format('YYYY') }}
      {...commonScreenProps}
    />
  )
}

const mapStateToProps = state => ({
  targetInfo: getApiTargetInfo(state),
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  units: getUnits(state),
  CLIENT: getClientInfo(state),
  unitNameMap: getUnitNameMap(state),
})

export default connect(mapStateToProps)(CommonMarginImport)
